import React, { ReactNode, useEffect, createContext, useContext } from 'react';

export type AppTheme = 'koordinator' | 'kloutforce' | 'katalog' | 'admin' | 'light' | 'dark';

// Theme Context
const ThemeContext = createContext<
  | {
      theme: AppTheme;
      setTheme?: (theme: AppTheme) => void;
    }
  | undefined
>(undefined);

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

interface ThemeTokens {
  primary: string;
  'primary-foreground': string;
  secondary: string;
  'secondary-foreground': string;
  accent: string;
  'accent-foreground': string;
}

// Theme token definitions (HSL values) - aligned with globals.css
const themeTokens: Record<AppTheme, Partial<ThemeTokens>> = {
  koordinator: {
    primary: '210 79% 46%',
    'primary-foreground': '0 0% 100%',
    secondary: '210 40% 96%',
    'secondary-foreground': '220 14% 16%',
    accent: '210 40% 94%',
    'accent-foreground': '220 14% 16%',
  },
  kloutforce: {
    primary: '335 78% 55%',
    'primary-foreground': '0 0% 100%',
    secondary: '335 25% 80%',
    'secondary-foreground': '335 10% 18%',
    accent: '335 60% 65%',
    'accent-foreground': '0 0% 100%',
  },
  katalog: {
    primary: '263 70% 55%',
    'primary-foreground': '0 0% 100%',
    secondary: '260 25% 80%',
    'secondary-foreground': '260 10% 18%',
    accent: '263 48% 86%',
    'accent-foreground': '0 0% 0%',
  },
  admin: {
    primary: '200 85% 40%',
    'primary-foreground': '0 0% 100%',
    secondary: '200 25% 75%',
    'secondary-foreground': '200 15% 12%',
    accent: '15 80% 55%',
    'accent-foreground': '0 0% 100%',
  },
  light: {
    // Uses defaults from globals.css
  },
  dark: {
    // Uses defaults from globals.css
  },
};

interface ThemeProviderProps {
  theme?: AppTheme;
  defaultTheme?: AppTheme;
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ theme, defaultTheme, children }) => {
  const activeTheme = theme || defaultTheme || 'light';
  useEffect(() => {
    const root = document.documentElement;

    // Set data-theme attribute for CSS targeting
    root.setAttribute('data-theme', activeTheme);

    // Apply theme-specific CSS custom properties
    const tokens = themeTokens[activeTheme];
    if (tokens) {
      Object.entries(tokens ?? {}).forEach(([key, value]) => {
        root.style.setProperty(`--${key}`, value);
      });
    }

    // Add theme class for additional styling
    root.classList.remove('koordinator', 'kloutforce', 'katalog', 'admin', 'light', 'dark');
    root.classList.add(activeTheme);
  }, [activeTheme]);

  return <ThemeContext.Provider value={{ theme: activeTheme }}>{children}</ThemeContext.Provider>;
}
